import { Formik, Form, Field } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Box from "../components/Box/Box";
import Progressbar from "../components/Progressbar/Progressbar";
import ErrorList from "../components/Form/ErrorList";
import { useEffect, useRef } from "react";
import { handleSubmitClick } from "../components/Form/FormUtils";
import BackButton from "../components/Buttons/BackButton";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import InputText from "../components/Form/InputText";
import { WITH_APP } from "./TicketType";

export const ADULT = "adult";
export const NOT_ADULT = "not_adult";
export const APPEAL_FOR_MYSELF = "appelformyself";
export const PARENT = "parent";
export const FIDUCIARY = "fiduciary";
export const HAVE_FIDUCIARY = "havefiduciary";

export default function ContactDetails() {
    const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
    const [formData, setFormData] = useAtom(formDataAtom);
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);

    return (
        <div>
            <Formik<FormModel>
                onSubmit={(values) => {
                    setFormData({
                        ...formData,
                        ...values,
                    });

                    navigate("/sammanfattning");
                }}
                initialValues={{
                    ...formData,
                    phone:
                        formData.ticketType === WITH_APP
                            ? formData.phone || formData.ticketNumber || ""
                            : formData.phone || "",

                }}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={Yup.object().shape({

                    firstname: Yup.string().when(["agecheck", "appealforwho"], {
                        is: (agecheck: string, appealforwho: string) =>
                            agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
                        then: (schema) => schema.required("Vänligen fyll i förnamn"),
                        otherwise: (schema) => schema.notRequired(),
                    }),

                    lastname: Yup.string().when(["agecheck", "appealforwho"], {
                        is: (agecheck: string, appealforwho: string) =>
                            agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
                        then: (schema) => schema.required("Vänligen fyll i efternamn"),
                        otherwise: (schema) => schema.notRequired(),
                    }),

                    phone: Yup.string().when(["agecheck", "appealforwho"], {
                        is: (agecheck: string, appealforwho: string) =>
                            agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
                        then: (schema) => schema.required("Vänligen fyll i telefonnummer"),
                        otherwise: (schema) => schema.notRequired(),
                    }),

                    email: Yup.string().when(["agecheck", "appealforwho"], {
                        is: (agecheck: string, appealforwho: string) =>
                            agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
                        then: (schema) => schema.required("Vänligen fyll i mejladress till vårdnadshavare, god man eller ombud.").email("Vänligen ange en korrekt mejladress"),
                        otherwise: (schema) => schema.notRequired(),
                    }),

                    reporterEmail: Yup.string().when(["agecheck", "appealforwho"], {
                        is: (agecheck: string, appealforwho: string) =>
                            agecheck === NOT_ADULT ||
                            (agecheck === ADULT && [FIDUCIARY, HAVE_FIDUCIARY, PARENT].includes(appealforwho)),
                        then: (schema) => schema.required("Vårdnashavare fyll i resenärens mejladress").email("Vänligen ange en korrekt mejladress"),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                })}
            >
                {({ errors, values }) => (
                    <>

                        <div className="flex justify-end items-center">
                            <div className="w-40 xs:w-52 md:w-full max-w-xs">
                                <Progressbar percentage={"88"} />
                            </div>
                        </div>
                        <Form noValidate>
                            <Box>
                                <h2 ref={h2Ref} tabIndex={-1} id="contactDetailsHeading">
                                    Kontaktuppgifter
                                </h2>

                                {values.agecheck === ADULT && values.appealforwho === APPEAL_FOR_MYSELF &&
                                    <>
                                        <h3 ref={h2Ref} tabIndex={-1} id="travelerHeading">
                                            Resenär
                                        </h3>

                                        {Object.keys(errors).length > 0 && <ErrorList errors={errors} />}

                                        <InputText
                                            name="firstname"
                                            label={<>Förnamn <span>(Obligatoriskt)</span></>}
                                            type="text"
                                            autocomplete={"given-name"}
                                        />

                                        <InputText
                                            name="lastname"
                                            label={<>Efternamn <span>(Obligatoriskt)</span></>}
                                            type="text"
                                            autocomplete={"family-name"}
                                        />

                                        <InputText
                                            name="phone"
                                            label={<>Telefonnummer <span>(Obligatoriskt)</span></>}
                                            type="text"
                                            autocomplete={"text"}
                                            numbersOnly
                                        />

                                        <InputText
                                            name="email"
                                            label={<>Mejladress <span>(Obligatoriskt)</span></>}
                                            type="email"
                                            autocomplete={"email"}
                                        />
                                    </>
                                }

                                {(values.agecheck === NOT_ADULT || (values.agecheck === ADULT && [FIDUCIARY, HAVE_FIDUCIARY, PARENT].includes(values.appealforwho))) &&
                                    <>
                                        <p>
                                            Fyll i kontaktuppgifter både till resenären och till en vårdnadshavare/förälder, god man eller ett ombud.                                        </p>

                                        <h3 className="mb-6" id="haveFiduciaryTravelerHeading">
                                            Resenär
                                        </h3>

                                        {Object.keys(errors).length > 0 && <ErrorList errors={errors} />}

                                        <InputText
                                            name="firstname"
                                            label={<>Resenärens förnamn <span>(Obligatoriskt)</span></>}
                                            type="text"
                                            autocomplete={"given-name"}
                                        />

                                        <InputText
                                            name="lastname"
                                            label={<>Resenärens efternamn <span>(Obligatoriskt)</span></>}
                                            type="text"
                                            autocomplete={"family-name"}
                                        />

                                        <InputText
                                            name="phone"
                                            label={<>Resenärens telefonnummer <span>(Obligatoriskt)</span></>}

                                            type="text"
                                            autocomplete={"text"}
                                            numbersOnly
                                        />

                                        <h3 id="haveFiduciaryHeading">
                                            Vårdnadshavare/förälder/god man/ombud
                                        </h3>

                                        <p>
                                            För att kunna behandla överklagan behöver du uppge en mejladress till en vårdnadshavare/förälder, 
                                            god man eller ombud, som är över 18 år.
                                        </p>

                                        <InputText
                                            name="reporterEmail"
                                            label={<>Mejladress för kontakt <span>(Obligatoriskt)</span></>}
                                            type="email"
                                            autocomplete={"email"}
                                        />
                                    </>
                                }
                            </Box>

                            <div className="flex justify-center">
                                <div className="w-full text-left flex items-center">
                                    <BackButton />
                                </div>
                                <div className="w-full text-right">
                                    <button
                                        onClick={handleSubmitClick(formRef)}
                                        className="btn primary"
                                        type="submit"
                                    >
                                        Gå vidare
                                    </button>
                                </div>
                            </div>

                        </Form>
                    </>
                )}
            </Formik>
        </div>
    );
}
